import type { AuthSession } from '../components/authSessionStore'

export type OAuthPopupProvider = 'wechat'

export interface OAuthPopupContext {
  provider: OAuthPopupProvider
  openerOrigin: string
  state: string
  redirectUri: string
  keep: boolean
  keepDays: number
}

export interface OAuthPopupSuccessMessage {
  source: 'imockup-oauth'
  type: 'success'
  provider: OAuthPopupProvider
  session: AuthSession
}

export interface OAuthPopupFailureMessage {
  source: 'imockup-oauth'
  type: 'failure'
  provider: OAuthPopupProvider
  message: string
}

export type OAuthPopupMessage = OAuthPopupSuccessMessage | OAuthPopupFailureMessage

const MESSAGE_SOURCE = 'imockup-oauth'
const OPENER_ORIGIN_KEY = 'imockup_oauth_popup_opener_origin'
const POPUP_CONTEXT_PREFIX = 'imockup-oauth-context:'

interface PopupSize {
  width: number
  height: number
}

export function rememberOAuthPopupOpenerOrigin(): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(OPENER_ORIGIN_KEY, window.location.origin)
  } catch {
    /* ignore */
  }
}

export function clearOAuthPopupOpenerOrigin(): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.removeItem(OPENER_ORIGIN_KEY)
  } catch {
    /* ignore */
  }
}

function readOAuthPopupOpenerOrigin(): string | null {
  if (typeof window === 'undefined') return null
  try {
    return window.localStorage.getItem(OPENER_ORIGIN_KEY)
  } catch {
    return null
  }
}

function resolvePostMessageTargetOrigin(): string {
  return readOAuthPopupOpenerOrigin() || (typeof window !== 'undefined' ? window.location.origin : '*')
}

export function openCenteredOAuthPopup(
  url: string,
  provider: OAuthPopupProvider,
  size: PopupSize,
  context?: OAuthPopupContext,
): Window | null {
  if (typeof window === 'undefined') return null
  const width = Math.min(size.width, Math.max(360, window.screen.availWidth - 40))
  const height = Math.min(size.height, Math.max(480, window.screen.availHeight - 40))
  const dualScreenLeft = window.screenLeft ?? window.screenX ?? 0
  const dualScreenTop = window.screenTop ?? window.screenY ?? 0
  const viewportWidth = window.outerWidth || document.documentElement.clientWidth || window.screen.availWidth
  const viewportHeight = window.outerHeight || document.documentElement.clientHeight || window.screen.availHeight
  const left = Math.max(0, Math.round(dualScreenLeft + (viewportWidth - width) / 2))
  const top = Math.max(0, Math.round(dualScreenTop + (viewportHeight - height) / 2))
  const features = [
    `width=${Math.round(width)}`,
    `height=${Math.round(height)}`,
    `left=${left}`,
    `top=${top}`,
    'popup=yes',
    'resizable=yes',
    'scrollbars=yes',
    'status=no',
    'toolbar=no',
    'menubar=no',
    'location=yes',
  ].join(',')
  const popup = window.open('', `imockup-oauth-${provider}`, features)
  if (!popup) return null
  if (context) {
    try {
      popup.name = `${POPUP_CONTEXT_PREFIX}${JSON.stringify(context)}`
    } catch {
      /* ignore */
    }
  }
  try {
    popup.location.replace(url)
  } catch {
    popup.location.href = url
  }
  return popup
}

export function isOAuthPopupMessage(data: unknown): data is OAuthPopupMessage {
  if (!data || typeof data !== 'object') return false
  const message = data as Partial<OAuthPopupMessage>
  return message.source === MESSAGE_SOURCE && (message.type === 'success' || message.type === 'failure')
}

export function readOAuthPopupContext(): OAuthPopupContext | null {
  if (typeof window === 'undefined') return null
  const raw = String(window.name || '')
  if (!raw.startsWith(POPUP_CONTEXT_PREFIX)) return null
  try {
    const parsed = JSON.parse(raw.slice(POPUP_CONTEXT_PREFIX.length)) as OAuthPopupContext
    if (
      parsed &&
      parsed.provider === 'wechat' &&
      typeof parsed.openerOrigin === 'string' &&
      typeof parsed.state === 'string' &&
      typeof parsed.redirectUri === 'string' &&
      typeof parsed.keep === 'boolean' &&
      typeof parsed.keepDays === 'number'
    ) {
      return parsed
    }
  } catch {
    /* ignore */
  }
  return null
}

export function clearOAuthPopupContext(): void {
  if (typeof window === 'undefined') return
  try {
    if (String(window.name || '').startsWith(POPUP_CONTEXT_PREFIX)) window.name = ''
  } catch {
    /* ignore */
  }
}

export function postOAuthPopupSuccess(provider: OAuthPopupProvider, session: AuthSession): boolean {
  if (typeof window === 'undefined' || !window.opener) return false
  const context = readOAuthPopupContext()
  const message: OAuthPopupSuccessMessage = {
    source: MESSAGE_SOURCE,
    type: 'success',
    provider,
    session,
  }
  window.opener.postMessage(message, context?.openerOrigin || resolvePostMessageTargetOrigin())
  clearOAuthPopupContext()
  clearOAuthPopupOpenerOrigin()
  return true
}

export function postOAuthPopupFailure(provider: OAuthPopupProvider, messageText: string): boolean {
  if (typeof window === 'undefined' || !window.opener) return false
  const context = readOAuthPopupContext()
  const message: OAuthPopupFailureMessage = {
    source: MESSAGE_SOURCE,
    type: 'failure',
    provider,
    message: messageText,
  }
  window.opener.postMessage(message, context?.openerOrigin || resolvePostMessageTargetOrigin())
  clearOAuthPopupContext()
  clearOAuthPopupOpenerOrigin()
  return true
}
